import { useCallback, useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { ApiError } from '@api/apiRuntime';
import { CalendarLayoutModelDetailOutDTO } from '@api/ringil-api.v2';
import { CalendarLayoutConfig } from '../model';
import { BUILT_IN_LAYOUT_CONFIG } from '../defaults';
import { configFromPayload, payloadFromConfig } from '../payload';

/**
 * Everything one model's editing screen needs: the detail, the layout being edited, and the
 * draft/publish/revert actions.
 *
 * The backend keeps a single draft per model alongside the published payload, so "what am I
 * editing" is: the draft if there is one, otherwise the published layout. Local edits are held here
 * until saved, which is why `dirty` is tracked separately from `hasDraft`.
 */
export const useCalendarLayoutModel = (modelId: string | null, onChanged?: () => void) => {
  const { getCalendarLayoutModel, saveDraft, discardDraft, publish, revert } = useContext(ApiContext);

  const [detail, setDetail] = useState<CalendarLayoutModelDetailOutDTO | null>(null);
  const [config, setConfig] = useState<CalendarLayoutConfig>(BUILT_IN_LAYOUT_CONFIG);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  /** A model whose payloads this build cannot parse still edits — from the built-in layout. */
  const configOf = (data: CalendarLayoutModelDetailOutDTO): CalendarLayoutConfig =>
    configFromPayload(data.draftPayload) ?? configFromPayload(data.publishedPayload) ?? BUILT_IN_LAYOUT_CONFIG;

  const adopt = useCallback(
    (data: CalendarLayoutModelDetailOutDTO) => {
      setDetail(data);
      setConfig(configOf(data));
      setDirty(false);
      setBusy(false);
      onChanged?.();
    },
    [onChanged],
  );

  const load = useCallback(() => {
    if (!modelId) {
      setDetail(null);
      setDirty(false);
      return;
    }
    setLoading(true);
    getCalendarLayoutModel(modelId, data => {
      setDetail(data);
      setConfig(configOf(data));
      setDirty(false);
      setLoading(false);
    });
    // `adopt` is deliberately not used here: loading must not fire onChanged and re-run the caller's
    // list refresh on every open.
  }, [modelId, getCalendarLayoutModel]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (next: CalendarLayoutConfig) => {
    setConfig(next);
    setDirty(true);
  };

  /**
   * Releases the busy flag on failure. Without it a single failed save left every action on the
   * screen disabled until the page was reloaded — `adopt` only runs on success. The standard error
   * toast still shows: `showStandardError` is called rather than swallowed.
   */
  const releaseOnError = { onError: (_error: ApiError, showStandardError: () => void) => { showStandardError(); setBusy(false); } };

  const save = () => {
    if (!modelId) return;
    setBusy(true);
    saveDraft(modelId, { payload: payloadFromConfig(config) }, adopt, undefined, releaseOnError);
  };

  /** Saves first when there are unsaved edits, so "publish" never quietly publishes stale content. */
  const publishModel = () => {
    if (!modelId) return;
    setBusy(true);
    const doPublish = () => publish(modelId, adopt, undefined, releaseOnError);
    if (dirty) saveDraft(modelId, { payload: payloadFromConfig(config) }, doPublish, undefined, releaseOnError);
    else doPublish();
  };

  const discard = () => {
    if (!modelId) return;
    setBusy(true);
    discardDraft(modelId, adopt, undefined, releaseOnError);
  };

  const revertModel = () => {
    if (!modelId) return;
    setBusy(true);
    revert(modelId, adopt, undefined, releaseOnError);
  };

  return {
    detail,
    config,
    dirty,
    loading,
    busy,
    /** True when the server holds a draft — published content and edited content differ. */
    hasDraft: !!detail?.draftPayload,
    change,
    save,
    publish: publishModel,
    discard,
    revert: revertModel,
    reload: load,
  };
};
